import { useAuth } from '@clerk/clerk-react';
import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

export type TranslationJobItemKind = 'attraction' | 'theme' | 'tour';
export type TranslationJobItemPhase = 'description' | 'voiceover';
export type TranslationJobItemStatus =
  | 'queued'
  | 'running'
  | 'done'
  | 'failed'
  | 'skipped';
export type TranslationJobStatus =
  | 'planning'
  | 'running'
  | 'completed'
  | 'failed';

export interface TranslationJobItem {
  id: string;
  kind: TranslationJobItemKind;
  phase: TranslationJobItemPhase;
  entityId: string;
  entityName: string;
  languageId: string;
  languageName: string;
  status: TranslationJobItemStatus;
  error?: string;
  startedAt?: string;
  finishedAt?: string;
}

export interface TranslationJobState {
  jobId: string;
  tourId: string;
  status: TranslationJobStatus;
  items: TranslationJobItem[];
  createdAt: string;
  updatedAt: string;
  error?: string;
}

interface UseTourTranslationJobSocketResult {
  state: TranslationJobState | null;
  isConnecting: boolean;
  error: string | null;
}

const NAMESPACE_PATH = '/tour-translation-job';

/**
 * Subscribe to live updates for a bulk translation job over Socket.IO.
 *
 * Connects to the `/tour-translation-job` namespace, fetches a Clerk JWT for
 * auth, joins the per-job room, and listens for `translation-job:state`
 * broadcasts. The first emission comes from the join ack so a freshly-opened
 * dialog or a reconnect immediately gets the current state without a poll.
 *
 * @param jobId - The job to subscribe to. Pass `null` to disable the hook.
 */
export function useTourTranslationJobSocket(
  jobId: string | null | undefined,
): UseTourTranslationJobSocketResult {
  const { getToken } = useAuth();
  const [state, setState] = useState<TranslationJobState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!jobId) {
      setState(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsConnecting(true);
    setError(null);
    setState(null);

    void (async () => {
      const token = await getToken();
      if (cancelled) return;

      if (!token) {
        setError('Not authenticated');
        setIsConnecting(false);
        return;
      }

      const socket = io(NAMESPACE_PATH, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        socket.emit(
          'translation-job:join',
          { jobId },
          (response: { state?: TranslationJobState; error?: string }) => {
            if (cancelled) return;
            setIsConnecting(false);
            if (response.error) {
              setError(response.error);
              return;
            }
            if (response.state) setState(response.state);
          },
        );
      });

      socket.on('translation-job:state', (next: TranslationJobState) => {
        if (cancelled) return;
        // Server may broadcast updates for any job in the namespace; filter
        // to the one we're tracking. (Belt-and-suspenders — server already
        // scopes by room.)
        if (next.jobId !== jobId) return;
        setState(next);
      });

      socket.on('connect_error', (err) => {
        if (cancelled) return;
        setError(err.message || 'Socket connection failed');
        setIsConnecting(false);
      });

      socket.on('disconnect', () => {
        if (cancelled) return;
        // Reconnect is automatic; the new `connect` handler will re-join.
      });
    })();

    return () => {
      cancelled = true;
      const socket = socketRef.current;
      if (socket) {
        socket.emit('translation-job:leave', { jobId });
        socket.disconnect();
        socketRef.current = null;
      }
    };
  }, [jobId, getToken]);

  return { state, isConnecting, error };
}
